import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen, Mic, Users, Briefcase, Plane, Award, ArrowRight,
} from "lucide-react";
import { AnimatedHero } from "@/components/AnimatedHero";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const services = [
  { icon: BookOpen, title: "IELTS", tag: "Academic & General", color: "#D90429", to: "/coaching" },
  { icon: Award, title: "PTE Academic", tag: "Computer-based", color: "#14213D", to: "/coaching" },
  { icon: Mic, title: "Spoken English", tag: "Fluency + Grammar", color: "#D90429", to: "/coaching" },
  { icon: Users, title: "Personality Development", tag: "Confidence & Poise", color: "#FFC300", to: "/coaching" },
  { icon: Briefcase, title: "Interview Training", tag: "Visa · HR · Academic", color: "#14213D", to: "/coaching" },
  { icon: Plane, title: "Study Abroad", tag: "27+ countries", color: "#D90429", to: "/immigration" },
];

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goProtected = (to) => (e) => {
    e.preventDefault();
    if (user) {
      navigate(to);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(to)}`);
    }
  };
  return (
    <div
      className="min-h-screen overflow-x-hidden relative"
      style={{
        background:
          "linear-gradient(115deg, #FFE1CC 0%, #FFEED8 22%, #F5EBEE 46%, #E5E4FA 68%, #D2E3FB 100%)",
      }}
      data-testid="landing-root"
    >
      {/* Soft decorative blobs echoing the reference image */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-[#FFD4B8] opacity-40 blur-3xl" />
        <div className="absolute top-32 right-[-8rem] w-[560px] h-[560px] rounded-full bg-[#C7DAFB] opacity-45 blur-3xl" />
        <div className="absolute bottom-40 left-1/3 w-[420px] h-[420px] rounded-full bg-[#F1DFF8] opacity-40 blur-3xl" />
      </div>
      <div className="relative z-10">
      <Navbar />
      <AnimatedHero />

      {/* Marquee */}
      <div className="relative border-y border-brand-navy/10 bg-brand-navy py-4 overflow-hidden">
        <motion.div
          animate={{ x: [0, -1200] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="flex gap-16 whitespace-nowrap text-white/80 font-heading text-lg font-semibold"
        >
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-16 items-center">
              {["Canada 🇨🇦", "Australia 🇦🇺", "UK 🇬🇧", "USA 🇺🇸", "Germany 🇩🇪", "Ireland 🇮🇪"].map((t) => (
                <span key={t + k} className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-brand-gold" />
                  <span>{t}</span>
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Services */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger}
            className="max-w-2xl mb-16"
          >
            <motion.div variants={fadeUp} className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
              Our programs
            </motion.div>
            <motion.h2 variants={fadeUp} className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy leading-tight">
              Six programs. One goal — <span className="text-brand-red">your success.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-4 text-brand-navy/60 text-lg">
              Every course is built around small batches, weekly mocks, and mentors who&#39;ve been where you are heading.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={goProtected(s.to)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") goProtected(s.to)(e); }}
                className={`card-hover cursor-pointer group relative rounded-3xl p-8 border border-brand-navy/10 bg-white overflow-hidden ${
                  i === 1 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{ background: s.color }} />
                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl grid place-items-center mb-6"
                    style={{ background: s.color === "#FFC300" ? "#FFC30022" : s.color + "18", color: s.color }}
                  >
                    <s.icon className="w-7 h-7" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-brand-navy/50">{s.tag}</div>
                  <h3 className="mt-2 font-heading font-bold text-2xl text-brand-navy">{s.title}</h3>
                  <div className="mt-5 flex items-center text-brand-red font-semibold text-sm">
                    Explore
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Split CTA — Coaching vs Immigration */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl bg-brand-red text-white p-10 md:p-12 overflow-hidden brand-shadow"
          >
            <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-white/10" />
            <div className="absolute -top-16 right-1/3 w-40 h-40 rounded-full bg-brand-gold/30 blur-xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">Coaching</div>
              <h3 className="font-heading font-black text-3xl md:text-4xl mt-2 leading-tight">
                Score higher. Speak sharper. Interview like a pro.
              </h3>
              <p className="mt-4 text-white/80 max-w-md">
                IELTS, PTE and Spoken English batches — daily mocks, real classrooms, and mentors who care.
              </p>
              <Link to="/coaching" onClick={goProtected("/coaching")} data-testid="cta-coaching-btn">
                <Button className="mt-6 rounded-full bg-white text-brand-red hover:bg-brand-gold hover:text-brand-navy font-bold px-6 h-12 btn-scale">
                  Start Coaching <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl bg-brand-navy text-white p-10 md:p-12 overflow-hidden"
          >
            <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-brand-gold/20" />
            <div className="absolute top-6 right-6 w-24 h-24 rounded-full border-4 border-brand-gold/40" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-gold">Study Abroad</div>
              <h3 className="font-heading font-black text-3xl md:text-4xl mt-2 leading-tight">
                From SOP to boarding pass — we handle it all.
              </h3>
              <p className="mt-4 text-white/70 max-w-md">
                Country selection, university shortlisting, visa filing and pre-departure — with a 92% approval rate.
              </p>
              <Link to="/immigration" onClick={goProtected("/immigration")} data-testid="cta-immigration-btn">
                <Button className="mt-6 rounded-full bg-brand-gold text-brand-navy hover:bg-brand-gold-hover font-bold px-6 h-12 btn-scale">
                  Explore Study Abroad <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-brand-muted-bg relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-brand-red/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">Student stories</div>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl text-brand-navy">
              Real students. Real scores. <span className="text-brand-red">Real journeys.</span>
            </h2>
          </div>

          <ReviewsCarousel />
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
